import { useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import { useAuth0 } from '@auth0/auth0-react'
import pick from 'lodash/pick'
import { formatDate } from '../../../utils/formatting'
import {
  DATE_COLUMN_WIDTH,
  ID_COLUMN_WIDTH,
  NAME_COLUMN_MAX_WIDTH,
} from '../../../components/DataTable/DataTable.constants'

import { AppLayoutContext } from '../../../providers/AppLayout'
import { useUrlStateParams } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { TrailFinderAnswer, trailFinderAnswerFields } from '../../../models/TrailFinderAnswer.model'
import { Trail } from '../../../models/Trail.model'
import { TrailFinderQuestion } from '../../../models/TrailFinderQuestion.model'
import { TrailFinderAnswerRelations } from '../../../models/TrailFinderAnswerRelations.model'

import UserService from '../../../services/UserService/UserService'
import { TrailFinderAnswerService } from '../../../services/TrailFinderAnswerService/TrailFinderAnswerService'

import {
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DataTable from '../../../components/DataTable/DataTable.component'
import Loading from '../../../components/Loading/Loading.component'
import ImportExportDialog, {
  ImportExportDialogProps,
} from '../../../components/ImportExportDialog/ImportExportDialog.component'
import Link from '@mui/material/Link'
import TrailFinderAnswerForm from '../shared/TrailFinderAnswerForm.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AppStateContext } from '../../../providers/AppStateContext'

type TrailFinderAnswerSearchProps = {
  nested?: boolean
  trailFinderQuestion?: TrailFinderQuestion
  hideHeader?: boolean
  hideActions?: boolean
  disablePadding?: boolean
  excludeColumns?: string[]
  extraRowActions?: {
    label: string
    onClick: (row?: TrailFinderAnswer) => void
  }[]
}
const TrailFinderAnswerSearch = (props: TrailFinderAnswerSearchProps): JSX.Element => {
  const {
    nested,
    trailFinderQuestion,
    hideHeader = !trailFinderQuestion,
    hideActions = false,
    disablePadding = false,
    excludeColumns = [],
    extraRowActions = [],
  } = props

  const [isSaving, setIsSaving] = useState(false)

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailFinder])

  const [searchError, setSearchError] = useState('')
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const isActionsMenuOpen = Boolean(rowActionsAnchorEl)
  const [selectedRow, setSelectedRow] = useState<TrailFinderAnswer | undefined>()
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const [answer, setAnswer] = useUrlStateParams(
    '',
    'answer',
    (value) => encodeURIComponent(value.toString()),
    (value) => (value ? decodeURIComponent(value) : ''),
  )
  const answerInputRef = useRef<HTMLInputElement>()
  const [orderBy, setOrderBy] = useState<keyof TrailFinderAnswer | undefined>('sortWeight')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc' | undefined>('asc')
  const [page, setPage] = useUrlStateParams(
    1,
    'page',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 1),
  )
  const [pageSize, setPageSize] = useUrlStateParams(
    12,
    'page-size',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 12),
  )

  const [isImportExportOpen, setIsImportExportOpen] = useState(false)

  const trailFinderAnswerService = useMemo(() => new TrailFinderAnswerService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    if (!nested) {
      AppLayoutContext.setPageName('Trail Finder Answers')
    }
  }, [])

  const queryClient = useQueryClient()
  const { isLoading, data: trailFinderAnswersResponse } = useQuery(
    [
      'TrailFinderAnswerService.search',
      {
        questionId: trailFinderQuestion?.id,
        page,
        pageSize,
        answer,
        orderBy,
        orderDirection,
        relations: [],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailFinderAnswerService.search({
        questionId: trailFinderQuestion?.id,
        page,
        pageSize,
        answer,
        orderBy,
        orderDirection,
        relations: [],
      }),
    {
      onError: (err) => {
        setSearchError(typeof err === 'string' ? err : 'An error occurred while searching for trail finder answers.')
        console.error(err)
      },
    },
  )

  useEffect(() => {
    if (
      trailFinderAnswersResponse &&
      page > 1 &&
      (!trailFinderAnswersResponse.total || (page - 1) * pageSize >= trailFinderAnswersResponse.total)
    ) {
      // page out of range was requested so reset to page 1
      setPage(1)
    }
  }, [trailFinderAnswersResponse])

  const handleRowActionsClick = (event: React.MouseEvent<HTMLButtonElement>, trailFinderAnswer: TrailFinderAnswer) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedRow(trailFinderAnswer)
  }
  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
  }

  const columns = useMemo(() => {
    const columns: GridColDef<TrailFinderAnswer>[] = [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              id={`row-actions-button-${params.row.id}`}
              color="primary"
              aria-label="row actions"
              aria-controls={selectedRow?.id === params.row.id ? 'row-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={selectedRow?.id === params.row.id ? 'true' : undefined}
              onClick={(event) => handleRowActionsClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: ID_COLUMN_WIDTH,
        renderCell: (params) => (
          <Link color="primary" aria-label="trail finder answer detail" href={`/trail-finder-answers/${params.row.id}`}>
            {params.row.id}
          </Link>
        ),
      },
      {
        field: 'answer',
        headerName: 'Answer',
        // takes the space left over by the other columns so the table fits its container instead of scrolling
        flex: 1,
        minWidth: 160,
        maxWidth: NAME_COLUMN_MAX_WIDTH,
      },
      {
        field: 'sortWeight',
        headerName: 'Sort',
        width: 100,
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 100,
        renderCell: (params) => (params.row.active ? 'Yes' : 'No'),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: DATE_COLUMN_WIDTH,
        renderCell: (params) => formatDate(params.row.updatedAt),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: DATE_COLUMN_WIDTH,
        renderCell: (params) => formatDate(params.row.createdAt),
      },
    ]
    // remove any columns specified to be removed in the excludeColumns prop
    if (excludeColumns) {
      return columns.filter((column) => !excludeColumns.includes(column.field as string))
    }
    return columns
  }, [excludeColumns, hasWritePermissions])

  const handleSubmit = () => {
    setPage(1)
    setAnswer(answerInputRef?.current?.value || '')
  }

  const handleAddClick = () => {
    setIsAdding(true)
  }

  const handleAddCancel = () => {
    setIsAdding(false)
  }

  const handleAddSubmit = () => {
    setIsAdding(false)
  }

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleEditCancel = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleEditSubmit = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false)
    handleRowActionsClose()
  }

  const deleteTrailFinderAnswer = useMutation(
    (mutationParams: { id: number }) => {
      setIsSaving(true)
      return trailFinderAnswerService.delete(mutationParams.id)
    },
    {
      onSuccess: () => {
        SnackbarContext.show('Trail Finder Answer deleted successfully!', 'success', APP_UPDATE_NOTICE)
        setRowActionsAnchorEl(null)
        setSelectedRow(undefined)
        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSaving(false)
      },
      onError: (err) => {
        SnackbarContext.show(`Trail Finder Answer failed to delete: ${err}`, 'error')
        console.error(err)
        setIsSaving(false)
      },
    },
  )

  const handleDelete = () => {
    if (selectedRow?.id) {
      setIsDeleteConfirmationOpen(false)
      deleteTrailFinderAnswer.mutate({ id: selectedRow.id })
    }
  }

  const openImportExport = () => {
    setIsImportExportOpen(true)
  }

  const handleImportExportClose = () => {
    setIsImportExportOpen(false)
  }

  const handleImport: ImportExportDialogProps['onImport'] = (data: Partial<Trail>[], options) => {
    setIsImportExportOpen(false)
    if (!data) {
      SnackbarContext.show('Failed to read/parse file', 'error')
      console.error('Failed to read/parse file')
    } else {
      // enforce the questionId if one is available
      if (trailFinderQuestion?.id) {
        data = data.map((trailFinderAnswer) => ({ ...trailFinderAnswer, questionId: trailFinderQuestion.id }))
      }
      setIsSaving(true)
      trailFinderAnswerService
        .import(data, options)
        .then(() => {
          SnackbarContext.show('Trail Finder Answers imported successfully!', 'success', APP_UPDATE_NOTICE)

          // fetch new data
          setSearchError('')
          // invalidate all queries to ensure any queries that are using the modified record are updated
          queryClient.invalidateQueries()
          setIsSaving(false)
        })
        .catch((err) => {
          SnackbarContext.show(`Trail Finder Answers failed to import: ${err}`, 'error')
          console.error(err)
          setIsSaving(false)
        })
    }
  }

  const handleExport = () => {
    // do a request to the API to get the data to export with no pagination
    setIsSaving(true)
    setIsImportExportOpen(false)
    trailFinderAnswerService
      .search({
        questionId: trailFinderQuestion?.id,
        pageSize: 0,
        answer,
        orderBy,
        orderDirection,
        relations: [TrailFinderAnswerRelations.question],
      })
      .then((results) => {
        const exportData = results.data.map((trailFinderAnswer) => {
          const trailFinderAnswerToExport: Record<string, unknown> = { ...trailFinderAnswer }
          trailFinderAnswerToExport.questionId = trailFinderAnswer.question?.id
          delete trailFinderAnswerToExport.question

          // convert the affectedFilters object or null to a string
          trailFinderAnswerToExport.affectedFilters =
            trailFinderAnswerToExport.affectedFilters !== undefined
              ? JSON.stringify(trailFinderAnswerToExport.affectedFilters)
              : ''

          // convert the active boolean to a string
          trailFinderAnswerToExport.active = JSON.stringify(trailFinderAnswerToExport.active)

          trailFinderAnswerToExport.updatedAt =
            trailFinderAnswerToExport.updatedAt instanceof Date ? trailFinderAnswerToExport.updatedAt.toISOString() : ''
          trailFinderAnswerToExport.createdAt =
            trailFinderAnswerToExport.createdAt instanceof Date ? trailFinderAnswerToExport.createdAt.toISOString() : ''

          // return the object with the props in the order we want them to appear in the csv
          const propsToExport: string[] = [...trailFinderAnswerFields]
          // add questionId prop after answer
          propsToExport.splice(propsToExport.indexOf('answer') + 1, 0, 'questionId')
          return pick(trailFinderAnswerToExport, propsToExport)
        })
        const csvData = d3.csvFormat(exportData)
        // initiate a download of the csv file
        const blob = new Blob([csvData], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.setAttribute('href', url)
        link.setAttribute('download', 'trail-finder-answers.csv')
        link.click()
        setIsSaving(false)
      })
      .catch((err) => {
        SnackbarContext.show(`Failed to fetch trail finder answers for export: ${err}`, 'error')
        console.error(err)
      })
  }

  return (
    <>
      {selectedRow && (
        <FormDialog<TrailFinderAnswer>
          open={isEditing}
          entity={selectedRow}
          entityLabel="Trail Finder Answer"
          onClose={handleEditCancel}
        >
          <TrailFinderAnswerForm
            trailFinderAnswer={selectedRow}
            trailFinderQuestion={trailFinderQuestion}
            onEdit={handleEditSubmit}
            onCancel={handleEditCancel}
          />
        </FormDialog>
      )}
      <FormDialog<TrailFinderAnswer> open={isAdding} entityLabel="Trail Finder Answer" onClose={handleAddCancel}>
        <TrailFinderAnswerForm
          trailFinderQuestion={trailFinderQuestion}
          onAdd={handleAddSubmit}
          onCancel={handleAddCancel}
        />
      </FormDialog>
      <Dialog fullWidth maxWidth="xs" open={isDeleteConfirmationOpen} onClose={handleDeleteConfirmationClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to delete trail finder answer id {selectedRow?.id}: {selectedRow?.answer}?
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleDeleteConfirmationClose}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <ImportExportDialog
        open={isImportExportOpen}
        disableImport={!hasWritePermissions}
        exampleImportFileUrl="/example-import-files/trail-finder-answers.csv"
        importFields={[
          ...trailFinderAnswerFields.filter((field) => !['answers', 'updatedAt', 'createdAt'].includes(field)),
        ]}
        onImport={handleImport}
        onExport={handleExport}
        onClose={handleImportExportClose}
      />
      <Paper sx={{ p: disablePadding ? 0 : 2 }}>
        {!hideHeader && (
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Trail Finder Answers
          </Typography>
        )}
        {(isLoading || isSaving) && !searchError && <Loading sx={{ py: 20 }} />}
        {searchError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{searchError}</Typography>
          </Stack>
        )}
        {!isLoading && !isSaving && !searchError && (
          <>
            {!hideActions && (
              <Box sx={{ pt: 1, mb: 1 }}>
                <Stack direction={{ zero: 'column', md: 'row' }} spacing={1}>
                  <FormControl variant="outlined" sx={{ width: '100%' }}>
                    <InputLabel>Answer</InputLabel>
                    <OutlinedInput
                      id="search-answer"
                      label="Answer"
                      defaultValue={answer || ''}
                      inputRef={answerInputRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSubmit()
                        }
                      }}
                      endAdornment={
                        answerInputRef?.current?.value ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="clear search answer"
                              onClick={() => {
                                if (answerInputRef?.current?.value) {
                                  answerInputRef.current.value = ''
                                }
                                setPage(1)
                                setAnswer('')
                              }}
                              edge="end"
                            >
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined
                      }
                    />
                  </FormControl>

                  <Button
                    variant="outlined"
                    onClick={handleSubmit}
                    sx={(theme) => ({ [theme.breakpoints.up('md')]: { width: 150 }, maxWidth: '100%' })}
                  >
                    Search
                  </Button>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {hasWritePermissions && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        color="primary"
                        aria-label="add new"
                        onClick={handleAddClick}
                      >
                        Add
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<ImportExportIcon />}
                      color="primary"
                      aria-label="show import export"
                      onClick={() => {
                        openImportExport()
                      }}
                    >
                      Import / Export
                    </Button>
                  </Stack>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      if (answerInputRef?.current?.value) {
                        answerInputRef.current.value = ''
                      }
                      setPage(1)
                      setAnswer('')
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Box>
            )}

            {trailFinderAnswersResponse?.data && trailFinderAnswersResponse.data.length > 0 ? (
              <>
                <Menu
                  id="row-actions-menu"
                  anchorEl={rowActionsAnchorEl}
                  open={isActionsMenuOpen}
                  onClose={handleRowActionsClose}
                  MenuListProps={{
                    'aria-labelledby': `row-actions-button${selectedRow?.id}`,
                  }}
                >
                  <MenuItem href={`/trail-finder-answers/${selectedRow?.id}`} component={Link}>
                    View
                  </MenuItem>
                  {!hideActions && hasWritePermissions && <MenuItem onClick={handleEditClick}>Edit</MenuItem>}
                  {!hideActions && hasWritePermissions && <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>}
                  {Array.isArray(extraRowActions) && extraRowActions.length > 0 && <Divider sx={{ my: 0.5 }} />}
                  {Array.isArray(extraRowActions) &&
                    extraRowActions.length > 0 &&
                    extraRowActions.map((action, index) => (
                      <MenuItem key={index} onClick={() => action.onClick(selectedRow)}>
                        {action.label}
                      </MenuItem>
                    ))}
                </Menu>
                <DataTable
                  columns={columns}
                  data={trailFinderAnswersResponse.data}
                  total={trailFinderAnswersResponse.total}
                  paginationMode="server"
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(newPage) => {
                    setPage(newPage)
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize)
                  }}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  onOrderChange={(newOrder) => {
                    setOrderBy(newOrder.field ? (newOrder.field as keyof TrailFinderAnswer) : undefined)
                    setOrderDirection(newOrder.sort)
                  }}
                />
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>No results found</Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default TrailFinderAnswerSearch
